import { useEffect, useRef } from 'react';
import { useChat } from '@/hooks';
import { useAssistants } from '@/hooks/useAssistants';

/**
 * Shared hook for content pages with a chat panel.
 * Handles: startNewChat on mount, auto-select first assistant, scroll-to-bottom.
 *
 * @param resetKey - Optional string (e.g. slug or make/model). When it changes,
 *                   the chat resets. Defaults to undefined (reset on mount only).
 */
export const usePageChat = (resetKey?: string) => {
    const {
        messages,
        isLoading,
        streamingMessage,
        selectedAssistant,
        sendMessage,
        setSelectedAssistant,
        startNewChat,
        currentTool,
    } = useChat();

    const { assistants, loading: loadingAssistants } = useAssistants();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const assistant = assistants[selectedAssistant] || null;

    // Use a ref so the effect below doesn't re-fire when startNewChat identity changes
    const startNewChatRef = useRef(startNewChat);
    startNewChatRef.current = startNewChat;

    // Reset chat on mount (and when resetKey changes, e.g. slug navigation)
    useEffect(() => {
        startNewChatRef.current();
    }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-select first assistant when assistants finish loading
    useEffect(() => {
        if (!loadingAssistants && !selectedAssistant) {
            const ids = Object.keys(assistants);
            if (ids.length > 0) {
                setSelectedAssistant(ids[0]);
                startNewChat();
            }
        }
    }, [loadingAssistants, assistants, selectedAssistant, setSelectedAssistant, startNewChat]);

    // Scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamingMessage]);

    return {
        messages,
        isLoading,
        streamingMessage,
        sendMessage,
        assistant,
        loadingAssistants,
        messagesEndRef,
        currentTool,
    };
};
